import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Music, Sparkles, Trophy, ArrowRight } from 'lucide-react';

const PromoPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasSeenPopup, setHasSeenPopup] = useState(false);

  useEffect(() => {
    // Check if user has already seen the popup today
    const lastSeen = localStorage.getItem('promoPopupLastSeen');
    const today = new Date().toDateString();
    
    if (lastSeen !== today) {
      // Show popup after 2 seconds delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setHasSeenPopup(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Save that user has seen popup today
    localStorage.setItem('promoPopupLastSeen', new Date().toDateString());
  };

  const handleSubmitDemo = () => {
    window.open('https://voice-audition-app.preview.emergentagent.com', '_blank', 'noopener,noreferrer');
    handleClose();
  };

  if (hasSeenPopup) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
            onClick={handleClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div 
              className="relative w-full max-w-lg rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background with gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-light-pink via-mid-purple to-mid-blue opacity-90" />
              
              {/* Animated sparkles background */}
              <div className="absolute inset-0 overflow-hidden">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"
                />
              </div>

              {/* Content */}
              <div className="relative p-8 md:p-10 text-center">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-black/50 hover:text-black transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Trophy icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="mx-auto w-20 h-20 rounded-full bg-black flex items-center justify-center mb-6"
                >
                  <Trophy className="w-10 h-10 text-light-yellow" />
                </motion.div>

                {/* Sparkles decoration */}
                <div className="absolute top-8 left-8">
                  <Sparkles className="w-6 h-6 text-light-yellow animate-pulse" />
                </div>
                <div className="absolute top-16 right-12">
                  <Sparkles className="w-4 h-4 text-white/80 animate-pulse" />
                </div>
                <div className="absolute bottom-20 left-12">
                  <Music className="w-5 h-5 text-black/30" />
                </div>

                {/* Headline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="font-mono text-xs uppercase tracking-widest text-black/60 mb-2">
                    Exclusive Opportunity
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black uppercase mb-4">
                    Stand a Chance to Win
                  </h2>
                </motion.div>

                {/* Prize amount */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                  className="mb-6"
                >
                  <div className="inline-block bg-black rounded-xl px-6 py-4">
                    <p className="font-display text-5xl md:text-6xl font-extrabold text-light-pink">
                      R50,000
                    </p>
                    <p className="font-mono text-sm uppercase tracking-widest text-white/80 mt-1">
                      Recording Contract
                    </p>
                  </div>
                </motion.div>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-black/80 text-base md:text-lg mb-8 max-w-sm mx-auto"
                >
                  Submit your demo through our website and you could be the next artist signed to <span className="font-bold">LoxitonDeepHouse</span>!
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-col sm:flex-row gap-3 justify-center"
                >
                  <button
                    onClick={handleSubmitDemo}
                    className="inline-flex items-center justify-center bg-black text-white rounded-full px-8 py-4 font-mono text-sm uppercase tracking-widest hover:bg-black/80 transition-colors group"
                  >
                    Submit Your Demo
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={handleClose}
                    className="inline-flex items-center justify-center bg-white/30 text-black rounded-full px-6 py-4 font-mono text-sm uppercase tracking-widest hover:bg-white/50 transition-colors"
                  >
                    Maybe Later
                  </button>
                </motion.div>

                {/* Terms note */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-black/50 text-xs mt-6"
                >
                  Terms & conditions apply. Open to all artists.
                </motion.p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PromoPopup;
